/*
 * Retry + Circuit Breaker (shared core)
 *
 * Framework-agnostic version of the same pattern used for the rxjs/ajax
 * effects pipeline (see effects/resilience.js), so fetch/axios call sites
 * share one breaker per API host rather than tracking failures separately.
 *
 * Scoped to transport-level failures only (dropped connection, network
 * reset, timeout, DNS/CORS failure before any HTTP response arrives).
 * A real HTTP response (4xx/5xx) is never retried here since the server
 * already received the request — retrying those could duplicate
 * non-idempotent create/update calls.
 */

export const RETRY_DELAY_MS = 300
export const MAX_RETRIES = 1

const FAILURE_THRESHOLD = 5
const COOLDOWN_MS = 15 * 1000

const breakers = new Map()

const getBreaker = (key) => {
  if ( ! breakers.has(key)) {
    breakers.set(key, { state: 'closed', failures: 0, openedAt: 0 })
  }
  return breakers.get(key)
}

export const getHostKey = (url) => {
  try {
    return new URL(url, window.location.origin).host
  }
  catch (e) {
    return 'default'
  }
}

export const isTransportError = (error) => {
  if ( ! error) {
    return false
  }

  // rxjs/ajax: request never received an HTTP response
  if (error.status === 0 || error.name === 'AjaxTimeoutError' || error.name === 'TimeoutError') {
    return true
  }

  // axios: request went out but no response ever came back
  if (error.isAxiosError && ! error.response) {
    return true
  }

  // fetch: rejects with a TypeError when the network request itself fails
  const message = `${error?.message || ''}`
  return error.name === 'TypeError' || /failed to fetch|network ?error|load failed|err_connection_reset|timeout/i.test(message)
}

// True once a breaker has opened, until its cooldown elapses; that check
// itself flips the breaker to 'half-open' so the next request is a probe.
export const canAttempt = (key) => {
  const breaker = getBreaker(key)

  if (breaker.state === 'open') {
    if (Date.now() - breaker.openedAt >= COOLDOWN_MS) {
      breaker.state = 'half-open'
      return true
    }
    return false
  }

  return true
}

export const recordSuccess = (key) => {
  const breaker = getBreaker(key)
  breaker.state = 'closed'
  breaker.failures = 0
}

export const recordTransportFailure = (key) => {
  const breaker = getBreaker(key)
  breaker.failures += 1

  if (breaker.state === 'half-open' || breaker.failures >= FAILURE_THRESHOLD) {
    breaker.state = 'open'
    breaker.openedAt = Date.now()
  }
}

export class CircuitOpenError extends Error {
  constructor(key) {
    super(`[circuit open] ${key} is failing fast after repeated transport errors, retrying again shortly.`)
    this.name = 'CircuitOpenError'
  }
}

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/*
 * Runs `run` (a function returning a Promise, e.g. () => fetch(url, opts)
 * or () => axios(...)) guarded by the circuit breaker for `url`'s host,
 * retrying once on a transport-level failure with a short backoff.
 */
export const withResilience = async (url, run) => {
  const key = getHostKey(url)

  if ( ! canAttempt(key)) {
    throw new CircuitOpenError(key)
  }

  let attempt = 0
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const result = await run()
      recordSuccess(key)
      return result
    }
    catch (error) {
      const transport = isTransportError(error)

      if (attempt >= MAX_RETRIES || ! transport) {
        if (transport) {
          recordTransportFailure(key)
        }
        throw error
      }

      attempt += 1
      await wait(RETRY_DELAY_MS * attempt)
    }
  }
}
